import React, { memo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Check, BookPlus } from "lucide-react";
import { FuriganaText, SegmentedJapaneseText } from "./SubtitleTextComponents";
import type { SubtitleLine } from "@/types/subtitle";

// Define the props interface based on what the item needs
interface SubtitleItemProps {
  subtitle: SubtitleLine;
  isActive: boolean;
  isSaved: boolean;
  currentTime: number;
  isMobile: boolean;
  showFurigana: boolean;
  showChinese: boolean;
  showRomaji: boolean;
  showOnlyChinese: boolean;
  hideUnderline: boolean;
  hideGrammarBackground: boolean;
  hideShadowingSpotlight: boolean;
  highlightNewWords: boolean;
  shadowingMode: boolean;
  vocabularySet: Set<string>;
  tokenizer: any;
  enablePosTagging: boolean;
  fontSize: number;
  blurStrength: number;
  
  // Callbacks
  onSeek: (time: number) => void;
  onSaveSentence: (sub: SubtitleLine) => void;
  onWordClick: (word: string, rect: DOMRect) => void;
  onWordHover: (word: string, rect: DOMRect) => void;
  onPointerDown: (e: React.PointerEvent, time: number) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onMouseLeave: () => void;
}

// Helper to format time
const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const SubtitleItem = memo(function SubtitleItem({
  subtitle,
  isActive,
  isSaved,
  currentTime,
  isMobile,
  showFurigana,
  showChinese,
  showRomaji,
  showOnlyChinese,
  hideUnderline,
  hideGrammarBackground,
  hideShadowingSpotlight,
  highlightNewWords,
  shadowingMode,
  vocabularySet,
  tokenizer,
  enablePosTagging,
  fontSize,
  blurStrength,
  onSeek,
  onSaveSentence,
  onWordClick,
  onWordHover,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onMouseLeave
}: SubtitleItemProps) {
  
  // Only calculate progress if active to save resources
  const progress = isActive
    ? Math.max(
        0,
        Math.min(
          0.999,
          (currentTime - subtitle.startTime) /
            Math.max(0.001, subtitle.endTime - subtitle.startTime)
        )
      )
    : 0;

  return (
    <div
      data-subtitle-id={subtitle.id}
      className={cn(
        "group rounded-lg cursor-pointer transition-all duration-200 border touch-manipulation p-2 max-[390px]:p-1.5 sm:p-3",
        // CSS Optimization: removed problematic properties for iOS
        isActive
          ? "bg-primary/10 border-primary/30 shadow-sm"
          : "bg-background border-transparent hover:bg-muted/50 hover:border-border"
      )}
      // Removed content-visibility optimization as it causes blank rendering on iOS
      onClick={isMobile ? undefined : (e) => {
        // Simple click handler for desktop
        e.stopPropagation();
        const selection = window.getSelection();
        if (selection?.toString().trim()) return;
        onSeek(subtitle.startTime);
      }}
      onPointerDown={(e) => onPointerDown(e, subtitle.startTime)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onMouseLeave={onMouseLeave}
    >
      {/* Time stamp */}
      <div className="flex items-center justify-between mb-1 max-[390px]:mb-0.5 sm:mb-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            "h-auto px-1 max-[390px]:px-0.5 sm:px-2 py-0 max-[390px]:py-0 sm:py-1 text-[9px] sm:text-[10px] font-mono rounded",
            isActive
              ? "bg-primary text-primary-foreground hover:bg-primary/90"
              : "bg-muted text-muted-foreground hover:bg-muted/70"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onSeek(subtitle.startTime);
          }}
        >
          {formatTime(subtitle.startTime)}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-5 w-5 sm:h-6 sm:w-6 transition-opacity",
            isSaved 
              ? "opacity-100 text-emerald-500" 
              : isActive
                ? "opacity-100"
                : "opacity-0 sm:group-hover:opacity-100"
          )}
          onClick={(e) => {
            e.stopPropagation();
            onSaveSentence(subtitle);
          }}
        >
          {isSaved ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <BookPlus className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </Button>
      </div>

      {/* Japanese with furigana FIRST */}
      {!showOnlyChinese && (
        <div className={cn(
          "text-[13px] max-[390px]:text-[12.5px] sm:text-[15px] font-medium",
          isActive ? "text-primary" : "text-foreground/90"
        )}>
          {subtitle.furigana ? (
            <FuriganaText 
              segments={subtitle.furigana}
              japanese={subtitle.japanese}
              onWordClick={onWordClick}
              onWordHover={onWordHover}
              showFurigana={showFurigana}
              active={isActive}
              progress={progress}
              hideUnderline={hideUnderline}
              hideGrammarBackground={hideGrammarBackground}
              hideShadowingSpotlight={hideShadowingSpotlight}
              highlightNewWords={highlightNewWords}
              shadowingMode={shadowingMode}
              vocabularySet={vocabularySet}
              tokenizer={tokenizer}
              enablePosTagging={enablePosTagging}
              fontSize={fontSize}
              blurStrength={blurStrength}
            />
          ) : (
            <SegmentedJapaneseText 
              text={subtitle.japanese} 
              onWordClick={onWordClick}
              onWordHover={onWordHover}
              active={isActive}
              progress={progress}
              showFurigana={showFurigana}
              hideUnderline={hideUnderline}
              hideGrammarBackground={hideGrammarBackground}
              hideShadowingSpotlight={hideShadowingSpotlight}
              highlightNewWords={highlightNewWords}
              shadowingMode={shadowingMode}
              vocabularySet={vocabularySet}
              tokenizer={tokenizer}
              enablePosTagging={enablePosTagging}
              fontSize={fontSize}
              blurStrength={blurStrength}
            />
          )}
        </div>
      )}

      {/* Chinese translation SECOND */}
      {showChinese && (
        <div className={cn(
          "text-[12px] max-[390px]:text-[11px] sm:text-sm mt-0.5 max-[390px]:line-clamp-1",
          isActive ? "text-foreground" : "text-muted-foreground"
        )}>
          {subtitle.chinese}
        </div>
      )}

      {/* Romaji */}
      {showRomaji && subtitle.romaji && (
        <div className="text-[10px] sm:text-xs text-muted-foreground/70 italic mt-0.5">
          {subtitle.romaji}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo
  // Return true if props are equal (do NOT re-render)
  
  // 1. Static props that rarely change
  if (
    prevProps.subtitle.id !== nextProps.subtitle.id ||
    prevProps.isSaved !== nextProps.isSaved ||
    prevProps.isMobile !== nextProps.isMobile ||
    prevProps.showFurigana !== nextProps.showFurigana ||
    prevProps.showChinese !== nextProps.showChinese ||
    prevProps.showRomaji !== nextProps.showRomaji ||
    prevProps.showOnlyChinese !== nextProps.showOnlyChinese ||
    prevProps.hideUnderline !== nextProps.hideUnderline ||
    prevProps.hideGrammarBackground !== nextProps.hideGrammarBackground ||
    prevProps.hideShadowingSpotlight !== nextProps.hideShadowingSpotlight ||
    prevProps.highlightNewWords !== nextProps.highlightNewWords ||
    prevProps.shadowingMode !== nextProps.shadowingMode ||
    prevProps.enablePosTagging !== nextProps.enablePosTagging ||
    prevProps.fontSize !== nextProps.fontSize ||
    prevProps.blurStrength !== nextProps.blurStrength ||
    prevProps.tokenizer !== nextProps.tokenizer ||
    prevProps.vocabularySet !== nextProps.vocabularySet // Reference check is usually enough
  ) {
    return false;
  }

  // 2. Active state change is the most frequent trigger
  if (prevProps.isActive !== nextProps.isActive) {
    return false;
  }

  // 3. Time progress check
  // If NOT active, time changes don't matter -> return true (skip render)
  if (!nextProps.isActive) {
    return true; 
  }
  
  // If active, we need to check if time changed significantly enough to affect progress bar
  // or if we just want to update the progress highlight
  // For simplicity, if active, we always re-render to show smooth progress
  return false;
});
